import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SchoolMapPopup from './SchoolMapPopup';
import { SchoolMapItem } from './statistics/types';
import { getSchoolLevels, type SchoolLevel } from './schoolTypes';

// Fix default marker icons (Leaflet + bundler issue)
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

// Colored SVG marker per school level
const LEVEL_COLORS: Record<SchoolLevel | 'default', string> = {
    ala: '#4CAF50', // green
    yla: '#2196F3', // blue
    lukio: '#FF9800', // orange
    ammatillinen: '#9C27B0', // purple
    default: '#757575', // grey
};

const LEVEL_LABELS: Record<SchoolLevel | 'yhtenais', string> = {
    ala: 'A',
    yla: 'Y',
    lukio: 'L',
    ammatillinen: 'AM',
    yhtenais: 'YK',
};

const createColoredIcon = (color: string, label: string) =>
    L.divIcon({
        className: '',
        iconSize: [28, 40],
        iconAnchor: [14, 40],
        popupAnchor: [0, -36],
        html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
            <path d="M14 0C6.3 0 0 6.3 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.3 21.7 0 14 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>
            <text x="14" y="18" text-anchor="middle" fill="#fff" font-size="${label.length > 1 ? 9 : 11}" font-weight="bold" font-family="Arial,sans-serif">${label}</text>
        </svg>`,
    });

const YHTENAIS_COLOR = '#E91E63'; // pink

const iconCache = new Map<string, L.DivIcon>();

const getSchoolIcon = (school: SchoolMapItem): L.DivIcon => {
    const levels = school.school_type_code
        ? getSchoolLevels(school.school_type_code)
        : [];
    const isYhtenais = levels.length > 1;
    const key = isYhtenais ? 'yhtenais' : levels[0] || 'default';
    if (!iconCache.has(key)) {
        if (isYhtenais) {
            iconCache.set(
                key,
                createColoredIcon(YHTENAIS_COLOR, LEVEL_LABELS.yhtenais)
            );
        } else {
            const color =
                LEVEL_COLORS[key as SchoolLevel] || LEVEL_COLORS.default;
            const label =
                key === 'default' ? '?' : LEVEL_LABELS[key as SchoolLevel];
            iconCache.set(key, createColoredIcon(color, label));
        }
    }
    return iconCache.get(key)!;
};

// Finland center coordinates (adjusted slightly west to compensate for sidebar)
const FINLAND_CENTER: [number, number] = [64.0, 26.0];
const DEFAULT_ZOOM = 5;

const ResetButton = () => {
    const map = useMap();
    const handleReset = () => {
        map.setView(FINLAND_CENTER, DEFAULT_ZOOM);
    };
    return (
        <div className="leaflet-top leaflet-left" style={{ top: 80 }}>
            <div className="leaflet-control leaflet-bar">
                <a
                    href="#"
                    role="button"
                    title="Palauta oletusnäkymä"
                    aria-label="Palauta oletusnäkymä"
                    onClick={e => {
                        e.preventDefault();
                        handleReset();
                    }}
                    style={{
                        fontSize: 18,
                        fontWeight: 'bold',
                        lineHeight: '26px',
                    }}
                >
                    ⌂
                </a>
            </div>
        </div>
    );
};

interface SchoolMapProps {
    schools: SchoolMapItem[];
    height?: string | number;
}

const SchoolMap = ({ schools, height = '100%' }: SchoolMapProps) => {
    return (
        <MapContainer
            center={FINLAND_CENTER}
            zoom={DEFAULT_ZOOM}
            style={{ height, width: '100%' }}
            scrollWheelZoom={true}
        >
            <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ResetButton />
            <MarkerClusterGroup chunkedLoading>
                {schools.map(school => (
                    <Marker
                        key={school.guid}
                        position={[school.lat, school.lng]}
                        icon={getSchoolIcon(school)}
                    >
                        <Popup>
                            <SchoolMapPopup school={school} />
                        </Popup>
                    </Marker>
                ))}
            </MarkerClusterGroup>
        </MapContainer>
    );
};

export default SchoolMap;
